import React, { useCallback, useEffect, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { fetchServerStatusHistory, pingServer } from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";
import { formattedDate } from "src/helpers/DateHelpers";

import { Button, Tag, message } from 'antd';
import { SyncOutlined, WifiOutlined } from '@ant-design/icons';
import Card from "src/componets/Card";
import { shortFromNow } from "./_serverHelpers";

interface StatusEvent {
  state: string;
  crash: boolean;
  at: string;
}

interface StatusHistory {
  uptime24hPercent: number | null;
  uptime7dPercent: number | null;
  crashes7d: number;
  events: StatusEvent[];
}

interface Ping {
  online: boolean;
  motd?: string;
  version?: string;
  playersOnline?: number;
  playersMax?: number;
  latencyMs?: number;
}

const STATE_COLORS: { [key: string]: string } = {
  running:  'green',
  starting: 'blue',
  stopping: 'orange',
  offline:  'default'
};

const percent = (value: number | null | undefined) =>
  value === null || value === undefined ? '-' : `${value.toFixed(value >= 100 ? 0 : 1)}%`;

// Uptime %, crash count and the raw state events reported by Wings, plus
// an on-demand RakNet ping — does the server answer from outside, the
// way a player's client would ask.
const _StatusHistory = ({ server }: { server: ServerInterface }) => {
  const dispatch = useAppDispatch();

  const [history, setHistory] = useState<StatusHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [ping, setPing] = useState<Ping | null>(null);
  const [pinging, setPinging] = useState(false);

  const serverId = server.id;

  const load = useCallback(async () => {
    setLoading(true);
    const resultAction = await dispatch(fetchServerStatusHistory(serverId));
    setLoading(false);

    if (fetchServerStatusHistory.fulfilled.match(resultAction)) {
      setHistory(resultAction.payload);
    }
  }, [serverId, dispatch]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  async function runPing() {
    setPinging(true);
    const resultAction = await dispatch(pingServer(serverId));
    setPinging(false);

    if (pingServer.fulfilled.match(resultAction)) {
      setPing(resultAction.payload);
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Ping failed');
    }
  }

  return (
    <Card className='v-srv-availability'>
      <div className='o-head'>
        <h4 className='v-content-title'>Availability</h4>
        <Button
          className='o-icon-btn'
          icon={<SyncOutlined />}
          title='Refresh'
          aria-label='Refresh availability'
          loading={loading}
          onClick={() => load()}
        />
      </div>

      <div className='o-tiles'>
        <div className='o-tile'>
          <div className='o-value'>{percent(history?.uptime24hPercent)}</div>
          <div className='o-label'>Uptime 24h</div>
        </div>
        <div className='o-tile'>
          <div className='o-value'>{percent(history?.uptime7dPercent)}</div>
          <div className='o-label'>Uptime 7d</div>
        </div>
        <div className={`o-tile ${history?.crashes7d ? 'o-bad' : ''}`}>
          <div className='o-value'>{history ? history.crashes7d : '-'}</div>
          <div className='o-label'>Crashes 7d</div>
        </div>

        {/* Generated on click — a live RakNet ping, not a cached status. */}
        <div className='o-tile o-ping'>
          {ping
            ? <div className='o-value'>
                {ping.online
                  ? <>
                      <Tag color='green'>online</Tag>
                      {ping.latencyMs !== undefined && <span className='o-latency'>{ping.latencyMs} ms</span>}
                    </>
                  : <Tag color='red'>no answer</Tag>}
              </div>
            : <div className='o-value o-idle'>—</div>}
          <div className='o-label'>
            <Button
              size='small'
              icon={<WifiOutlined />}
              loading={pinging}
              onClick={runPing}
            >
              Ping now
            </Button>
          </div>
        </div>
      </div>

      {ping?.online && (ping.motd || ping.version) &&
        <div className='o-ping-detail'>
          {ping.motd && <span>{ping.motd}</span>}
          {ping.version && <span>v{ping.version}</span>}
          {ping.playersOnline !== undefined &&
            <span>{ping.playersOnline}{ping.playersMax ? `/${ping.playersMax}` : ''} players</span>}
        </div>
      }

      <div className='o-events'>
        {(history?.events || []).map((event, index) => (
          <div key={index} className={`o-event ${event.crash ? 'o-crash' : ''}`}>
            <Tag color={event.crash ? 'red' : STATE_COLORS[event.state] || 'default'}>
              {event.crash ? 'crash' : event.state}
            </Tag>
            <span className='o-when' title={formattedDate(event.at).dateTime}>
              {shortFromNow(event.at)}
            </span>
          </div>
        ))}

        {!loading && history && !history.events.length &&
          <div className='o-none'>No state changes recorded yet.</div>
        }
      </div>
    </Card>
  );
}

export default _StatusHistory;
